import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CellComponent, RowComponent, Tabulator } from 'tabulator-tables';
import useCameraManagement from '@/domains/asset-management/hooks/useCameraManagement';
import type {
  ButtonState,
  CameraDetailData,
  CameraFormValues,
  CreateCameraPayload,
  UpdateCameraPayload,
} from '@/domains/asset-management/types/camera';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { ReactTabulator } from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import TablePagination from '@/shared/components/ui/TablePagination/TablePagination';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

const commonColumnProps = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false as const,
};

// Columns whose `field` matches the backend allowed-sort whitelist keys (id | updated_time).
const CAMERA_SORT_FIELD_MAP: Record<string, string> = {
  cctv_id: 'id',
  updated_time: 'updated_time',
};

const emptyFormValues: CameraFormValues = {
  cctv_id: null,
  site_id: null,
  name: null,
  location_label: null,
  stream_url: null,
  device_status: null,
  latitude: null,
  longitude: null,
};

const CameraManagement = () => {
  const { t } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const [newId, setNewId] = useState<number | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<{ cctv_id: number | null }>({
    cctv_id: null,
  });
  const selectedCameraRef = useRef(selectedCamera);
  const [deviceStatusFilter, setDeviceStatusFilter] = useState<string>('');

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    selectedCameraRef.current = selectedCamera;
  }, [hasChangesUpdate, hasChangesCreate, selectedCamera]);

  const table = useServerTable<{ input?: string; device_status?: string }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;

  const { commonListData } = useCommonCodes({ optionParams: 'upper-code=DEV_ST' });
  const deviceStatusOptions = useMemo(
    () =>
      commonListData?.DEV_ST?.code?.map((code, index) => ({
        value: code ?? '',
        label: commonListData.DEV_ST?.name[index] ?? '',
      })) ?? [],
    [commonListData]
  );

  const [formValues, setFormValues] = useState<CameraFormValues>(emptyFormValues);

  const emptyDetail = useCallback(() => setFormValues(emptyFormValues), []);

  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({ confirm: true, cancel: true, delete: true, create: true });
  }, []);

  const enableInitialButtons = useCallback(() => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prev) => ({ ...prev, create: false }));
  }, [disableAllButtons]);

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false, delete: false, create: false }));
  }, [disableAllButtons]);

  const enableRegisterButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false }));
  }, [disableAllButtons]);

  const reloadCallback = useCallback(() => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedCamera({ cctv_id: null });
    setNewId(null);
  }, [enableInitialButtons, emptyDetail]);

  const {
    cameraListData,
    detailCameraData,
    deleteCamera,
    createCamera,
    updateCamera,
    refetchDetailCamera,
  } = useCameraManagement({
    cctvId: selectedCamera?.cctv_id,
    queryParams,
    onDeleteSuccess: reloadCallback,
    onUpdateSuccess: () => {
      refetchDetailCamera();
      enableUPDATEButtons();
      setIsNewClicked(false);
      setHasChangesUpdate(false);
    },
    onCreateSuccess: (responseData) => {
      reloadCallback();
      setNewId(responseData.cctv_id);
    },
  });

  const pageData = cameraListData?.data;
  const rows = pageData?.rows ?? [];
  const dataCamera = detailCameraData?.data;

  const cameraColumns = useMemo(
    () => [
      {
        title: t('cmn > no'),
        formatter: (cell: CellComponent) =>
          (table.page - 1) * table.size + cell.getRow().getPosition(true),
        width: 60,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('camera > camera name'),
        field: 'name',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnProps,
      },
      {
        title: t('cmn > latitude'),
        field: 'latitude',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('cmn > longitude'),
        field: 'longitude',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('DEV_ST'),
        field: 'device_status_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
    ],
    [table.page, table.size, t]
  );

  const detailToFormValues = useCallback(
    (data: CameraDetailData): CameraFormValues => ({
      cctv_id: data.cctv_id,
      site_id: data.site_id,
      name: data.name,
      location_label: data.location_label,
      stream_url: data.stream_url,
      device_status: data.device_status,
      latitude: data.latitude,
      longitude: data.longitude,
    }),
    []
  );

  useEffect(() => {
    if (dataCamera) {
      setFormValues(detailToFormValues(dataCamera));
    }
  }, [dataCamera, detailToFormValues]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormValues((prev) => ({ ...prev, [name]: value }));

      if (isNewClicked) {
        setHasChangesCreate(true);
      } else if (selectedCameraRef.current?.cctv_id) {
        setHasChangesUpdate(true);
      }
    },
    [isNewClicked]
  );

  const languageTabulator = () => ({
    pagination: {
      first: t('cmn > first page'),
      first_title: t('cmn > first page'),
      last: t('cmn > last page'),
      last_title: t('cmn > last page'),
      prev: t('cmn > page before'),
      prev_title: t('cmn > page before'),
      next: t('cmn > next page'),
      next_title: t('cmn > next page'),
    },
  });

  const optionsTabulator = {
    debugInvalidOptions: true,
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    selectableRows: false,
    movableRows: false,
    resizableRows: false,
    index: 'cctv_id',
    locale: 'ko',
    langs: { ko: languageTabulator() },
    selectableRowsCheck: (row: RowComponent) =>
      !row.getElement().classList.contains('tabulator-selected'),
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  const handleSearch = useCallback(
    (inputVal: string) => {
      table.setFilters({ input: inputVal || undefined });
    },
    [table]
  );

  const handleReset = useCallback(() => {
    setDeviceStatusFilter('');
    table.reset();
  }, [table]);

  const handleDeviceStatusFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setDeviceStatusFilter(e.target.value);
      table.setFilters({ device_status: e.target.value || undefined });
    },
    [table]
  );

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      const selectRow = () => {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedCamera({ cctv_id: rowData.cctv_id });
        setDisabledForm(false);
        setHasChangesUpdate(false);
        setHasChangesCreate(false);
        setIsNewClicked(false);
        enableUPDATEButtons();

        requestAnimationFrame(() => nameRef.current?.focus());
      };

      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
        message.confirmClicked().then(selectRow);
      } else {
        selectRow();
      }
    },
    [enableUPDATEButtons, t]
  );

  const handleNewButtonClick = useCallback(() => {
    const startNew = () => {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedCamera({ cctv_id: null });
      setDisabledForm(false);
      setHasChangesUpdate(false);
      requestAnimationFrame(() => nameRef.current?.focus());
    };

    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(startNew);
    } else {
      startNew();
    }
  }, [hasChangesCreate, hasChangesUpdate, enableRegisterButtons, t, emptyDetail]);

  const handleCancelButtonClick = useCallback(() => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setHasChangesUpdate(false);
        setSelectedCamera({ cctv_id: null });
        enableInitialButtons();
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
      }
    } else {
      reloadCallback();
    }
  }, [
    hasChangesUpdate,
    hasChangesCreate,
    isNewClicked,
    enableInitialButtons,
    reloadCallback,
    t,
    emptyDetail,
  ]);

  const buildPayload = useCallback((): CreateCameraPayload | null => {
    const { name, device_status, stream_url } = formValues;
    if (!name?.trim() || !device_status || !stream_url?.trim()) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > required values'));
      return null;
    }
    return {
      name: name.trim(),
      stream_url: stream_url.trim(),
      device_status,
      latitude:
        formValues.latitude === '' || formValues.latitude === null
          ? null
          : Number(formValues.latitude),
      longitude:
        formValues.longitude === '' || formValues.longitude === null
          ? null
          : Number(formValues.longitude),
    };
  }, [formValues, t]);

  const handleRegistButtonClick = useCallback(() => {
    const payload = buildPayload();
    if (!payload) return;
    createCamera(payload);
  }, [createCamera, buildPayload]);

  const handleConfirmButtonClick = useCallback(() => {
    const payload = buildPayload();
    if (!payload || !formValues.cctv_id) return;
    const updatePayload: UpdateCameraPayload = { ...payload, cctv_id: Number(formValues.cctv_id) };
    updateCamera({ cameraData: updatePayload, params: { cctv_id: updatePayload.cctv_id } });
  }, [formValues, updateCamera, buildPayload]);

  const handleDeleteButtonClick = useCallback(() => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), { mode: 'confirm' });
    message.confirmClicked().then(() => {
      if (selectedCamera?.cctv_id) deleteCamera(selectedCamera.cctv_id);
    });
  }, [selectedCamera, deleteCamera, t]);

  const logData = dataCamera
    ? [
        { label: t('cmn > registered by'), value: dataCamera.registered_by },
        {
          label: t('cmn > registered time'),
          value: dataCamera.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataCamera.registered_time))
            : dataCamera.registered_time,
        },
        { label: t('cmn > updated by'), value: dataCamera.updated_by },
        {
          label: t('cmn > updated time'),
          value: dataCamera.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataCamera.updated_time))
            : dataCamera.updated_time,
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>{t('ASSET')}</h3>
        <h3>&gt;</h3>
        <h3>{t('ASSET-CAMERA')}</h3>
      </div>

      <ContainerCard>
        <Filtering
          searchRef={searchRef}
          labelSelect={t('camera > filter by status')}
          placeholder={t('camera > search by camera name')}
          disableFiltering={true}
          onReset={handleReset}
          onSearch={handleSearch}
        >
          <Select
            value={deviceStatusFilter}
            onChange={handleDeviceStatusFilterChange}
            options={[{ value: '', label: t('cmn > all') }, ...deviceStatusOptions]}
          />
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          <ReactTabulator
            data={rows}
            columns={cameraColumns}
            layout="fitDataStretch"
            options={optionsTabulator}
            className="tabulator-custom"
            onRef={(r) => {
              tbRef.current = r.current;
            }}
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
              dataSorted: remoteSortHandler(table, CAMERA_SORT_FIELD_MAP),
              tableBuilt: () => {
                if (!tbRef.current || !newId) return;
                try {
                  const row = tbRef.current.getRow(newId) || null;
                  if (row && typeof row.select === 'function') {
                    tbRef.current.scrollToRow(row, 'bottom', true);
                    handleRowSelected(row);
                  }
                } catch {
                  /* row not found */
                }
              },
            }}
          />
        </div>
        <TablePagination
          total={pageData?.total ?? 0}
          page={table.page}
          size={table.size}
          totalPages={pageData?.total_pages ?? 0}
          onPageChange={table.setPage}
          centered
        />
      </ContainerCard>

      <ContainerCard className="flex flex-col">
        <div className="grid grid-cols-2 items-start gap-6">
          <div className="grid grid-cols-1 gap-3">
            <DetailForm
              className="items-center!"
              label={t('camera > camera name')}
              formRef={nameRef}
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.name || ''}
              name="name"
              maxLength={100}
            />
            <DetailForm
              className="items-center!"
              label={t('cmn > latitude longitude')}
              inputType="text"
              showInput={false}
            >
              <div className="flex w-full flex-row gap-x-2">
                <GeneralInput
                  customInput="w-full"
                  disabled={disabledForm}
                  onChange={handleInputChange}
                  value={formValues.latitude ?? ''}
                  name="latitude"
                  maxLength={100}
                  pattern={'^-?[0-9.]*$'}
                />
                <GeneralInput
                  customInput="w-full"
                  disabled={disabledForm}
                  onChange={handleInputChange}
                  value={formValues.longitude ?? ''}
                  name="longitude"
                  maxLength={100}
                  pattern={'^-?[0-9.]*$'}
                />
              </div>
            </DetailForm>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <DetailForm
              className="items-center!"
              label={t('camera > stream url')}
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.stream_url || ''}
              name="stream_url"
              maxLength={255}
            />
            <DetailForm
              className="items-center!"
              label={t('DEV_ST')}
              required={true}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.device_status || ''}
              name="device_status"
              optionSelect={deviceStatusOptions}
            />
          </div>
        </div>
        <hr className="border-t border-gray-300" />
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
          <div className="flex-none">
            <ButtonGroup
              onClickDelete={handleDeleteButtonClick}
              onClickNew={handleNewButtonClick}
              onClickCancel={handleCancelButtonClick}
              onClickRegist={handleRegistButtonClick}
              onClickConfirm={handleConfirmButtonClick}
              isNewClicked={isNewClicked}
              cancelButtonState={buttonState.cancel}
              confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
              deleteButtonState={buttonState.delete}
              newButtonState={hasChangesCreate ? false : buttonState.create}
            />
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default CameraManagement;
